import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import StarRateIcon from "@material-ui/icons/StarRate";
import ThumbUpAltOutlinedIcon from "@material-ui/icons/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@material-ui/icons/ThumbDownAltOutlined";
import SentimentSatisfiedIcon from "@material-ui/icons/SentimentSatisfied";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import ReplyOutlinedIcon from "@material-ui/icons/ReplyOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { PageHeader, StatCard, StatusBadge, Section, EmptyState, MainContainer } from "../../components/ui";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    scroll: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      ...theme.scrollbarStyles,
    },
    page: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
      maxWidth: 1280,
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      paddingBottom: theme.spacing(2),
    },
    pageHeader: { paddingTop: 0, paddingBottom: 0 },
    filters: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
    },
    filterDate: { width: 200, [theme.breakpoints.down("xs")]: { width: "100%" } },
    filterSelect: { width: 230, [theme.breakpoints.down("xs")]: { width: "100%" } },
    filterButton: { [theme.breakpoints.down("xs")]: { width: "100%" } },
    // rótulo dentro do campo ("De", "Atendente"): sem rótulo flutuante
    fieldPrefix: { flex: "none", fontSize: 13, color: t.textMuted, whiteSpace: "nowrap" },
    selectValue: { display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 },
    selectName: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sectionHint: {
      display: "block",
      marginTop: 2,
      fontSize: 13,
      fontWeight: 400,
      color: t.textSecondary,
    },
    tableWrap: {
      overflowX: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      minWidth: 720,
      "& th, & td": { whiteSpace: "nowrap" },
      // alinha a 1ª e a última coluna com o título do cartão (20 px)
      "& th:first-child, & td:first-child": { paddingLeft: 20 },
      "& th:last-child, & td:last-child": { paddingRight: 20 },
      "& tbody tr:last-child td": { borderBottom: 0 },
    },
    num: { fontWeight: 600 },
    numMuted: { color: t.textMuted },
    avg: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontWeight: 600,
    },
    avgStar: { fontSize: 16 },
    distribution: {
      display: "inline-flex",
      alignItems: "center",
      gap: 14,
      fontSize: 13,
      fontWeight: 600,
    },
    distItem: { display: "inline-flex", alignItems: "center", gap: 4 },
    kindIcon: { fontSize: 15, flex: "none" },
    ticketLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      color: t.brand,
      fontWeight: 600,
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    },
    contactName: { fontWeight: 500 },
    contactNumber: { fontSize: 12, color: t.textMuted },
    pager: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      padding: "10px 16px",
      borderTop: `1px solid ${t.line}`,
      fontSize: 13,
      color: t.textSecondary,
    },
    pagerButtons: { display: "flex", gap: 8 },
    loadingRow: {
      display: "flex",
      justifyContent: "center",
      padding: 32,
    },
  };
});

// Título de seção do painel com a linha de apoio embaixo (vai no `title` do Section).
const sectionTitle = (title, hint, classes) => (
  <>
    {title}
    {hint && <span className={classes.sectionHint}>{hint}</span>}
  </>
);

// Classificação da nota: ≥ 9 ótimo, 7–8 regular, ≤ 6 ruim (mesmo critério do backend / NPS).
const rateKind = (rate, label) => {
  const l = String(label || "").toLowerCase();
  if (l.startsWith("ót") || l.startsWith("ot") || l === "great" || l === "excelente") return "otimo";
  if (l.startsWith("reg")) return "regular";
  if (l.startsWith("ru") || l === "bad" || l === "malo") return "ruim";
  const n = Number(rate);
  if (!Number.isFinite(n)) return "regular";
  if (n >= 9) return "otimo";
  if (n >= 7) return "regular";
  return "ruim";
};

// Só apresentação: ícone e tom do painel (ok / atenção / crítico) de cada faixa de nota.
const KINDS = ["otimo", "regular", "ruim"];
const KIND_ICON = {
  otimo: ThumbUpAltOutlinedIcon,
  regular: SentimentSatisfiedIcon,
  ruim: ThumbDownAltOutlinedIcon,
};
const KIND_TONE = { otimo: "success", regular: "warning", ruim: "error" };
const KIND_STATUS = { otimo: "ok", regular: "warn", ruim: "down" };

const kindColor = (t, kind) => t.status[KIND_STATUS[kind] || "warn"].fg;

const pct = (part, total) => {
  const p = Number(part) || 0;
  const tt = Number(total) || 0;
  if (tt <= 0) return 0;
  return Math.round((p / tt) * 100);
};

const fmtAvg = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(1).replace(".", ",");
};

const fmtPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n)}%`;
};

const EMPTY_SUMMARY = {
  total: 0,
  avg: 0,
  otimo: 0,
  regular: 0,
  ruim: 0,
  closedTickets: 0,
  responseRate: 0,
  attendants: [],
};

const ALL = "";

const Ratings = () => {
  const classes = useStyles();
  const theme = useTheme();
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const { user } = useContext(AuthContext);

  const allowed = user?.profile === "admin" || user?.super === true;

  // filtros aplicados (o que foi buscado) e rascunho (o que está nos campos)
  const defaultFrom = moment().subtract(30, "days").format("YYYY-MM-DD");
  const defaultTo = moment().format("YYYY-MM-DD");
  const [draft, setDraft] = useState({ dateFrom: defaultFrom, dateTo: defaultTo, userId: ALL, queueId: ALL });
  const [filters, setFilters] = useState({ dateFrom: defaultFrom, dateTo: defaultTo, userId: ALL, queueId: ALL });

  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);

  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadingList, setLoadingList] = useState(true);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // listas dos filtros (atendentes e filas)
  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/users/list");
        if (active) setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        // filtro de atendente é opcional
      }
      try {
        const { data } = await api.get("/queue");
        if (active) setQueues(Array.isArray(data) ? data : []);
      } catch (err) {
        // filtro de fila é opcional
      }
    })();
    return () => {
      active = false;
    };
  }, [allowed]);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const { data } = await api.get("/ratings/summary", {
        params: { dateFrom: filters.dateFrom, dateTo: filters.dateTo },
      });
      if (!mountedRef.current) return;
      setSummary({
        ...EMPTY_SUMMARY,
        ...(data || {}),
        attendants: Array.isArray(data?.attendants) ? data.attendants : [],
      });
    } catch (err) {
      if (mountedRef.current) toastError(err);
    } finally {
      if (mountedRef.current) setLoadingSummary(false);
    }
  }, [filters.dateFrom, filters.dateTo]);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = { dateFrom: filters.dateFrom, dateTo: filters.dateTo, pageNumber };
      if (filters.userId !== ALL) params.userId = filters.userId;
      if (filters.queueId !== ALL) params.queueId = filters.queueId;
      const { data } = await api.get("/ratings", { params });
      if (!mountedRef.current) return;
      setRecords(Array.isArray(data?.records) ? data.records : []);
      setCount(Number(data?.count) || 0);
      setHasMore(Boolean(data?.hasMore));
    } catch (err) {
      if (mountedRef.current) toastError(err);
    } finally {
      if (mountedRef.current) setLoadingList(false);
    }
  }, [filters, pageNumber]);

  useEffect(() => {
    if (!allowed) return;
    fetchSummary();
  }, [allowed, fetchSummary]);

  useEffect(() => {
    if (!allowed) return;
    fetchList();
  }, [allowed, fetchList]);

  const applyFilters = () => {
    setPageNumber(1);
    setFilters({ ...draft });
  };

  const refresh = () => {
    fetchSummary();
    fetchList();
  };

  const attendants = useMemo(() => {
    const list = [...(summary.attendants || [])];
    list.sort((a, b) => (Number(b.avg) || 0) - (Number(a.avg) || 0) || (Number(b.count) || 0) - (Number(a.count) || 0));
    return list;
  }, [summary.attendants]);

  const cards = useMemo(
    () => [
      { label: i18n.t("ratings.cards.total"), value: summary.total || 0, icon: <ForumOutlinedIcon />, tone: "brand" },
      { label: i18n.t("ratings.cards.avg"), value: fmtAvg(summary.avg), icon: <StarRateIcon />, tone: "info" },
      {
        label: i18n.t("ratings.cards.otimo"),
        value: fmtPct(pct(summary.otimo, summary.total)),
        icon: <ThumbUpAltOutlinedIcon />,
        tone: "success",
      },
      {
        label: i18n.t("ratings.cards.ruim"),
        value: fmtPct(pct(summary.ruim, summary.total)),
        icon: <ThumbDownAltOutlinedIcon />,
        tone: summary.ruim > 0 ? "error" : "neutral",
      },
      {
        label: i18n.t("ratings.cards.responseRate"),
        value: fmtPct(summary.responseRate),
        icon: <ReplyOutlinedIcon />,
        tone: "brand",
      },
    ],
    [summary]
  );

  if (!allowed) {
    return <ForbiddenPage />;
  }

  const pageSize = 20;
  const from = count === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const to = Math.min(count, (pageNumber - 1) * pageSize + records.length);

  const prefixed = (prefix, text) => (
    <span className={classes.selectValue}>
      <span className={classes.fieldPrefix}>{prefix}</span>
      <span className={classes.selectName}>{text}</span>
    </span>
  );

  const datePrefix = (text) => ({
    startAdornment: (
      <InputAdornment position="start">
        <span className={classes.fieldPrefix}>{text}</span>
      </InputAdornment>
    ),
  });

  return (
    <MainContainer>
      <div className={classes.scroll}>
        <div className={classes.page}>
          <PageHeader
            className={classes.pageHeader}
            title={i18n.t("ratings.title")}
            subtitle={i18n.t("ratings.subtitle")}
            actions={
              <Button variant="outlined" onClick={refresh} startIcon={<RefreshIcon />}>
                {i18n.t("ratings.refresh")}
              </Button>
            }
          />

          {/* Filtros */}
          <div className={classes.filters}>
            <TextField
              size="small"
              variant="outlined"
              className={classes.filterDate}
              type="date"
              value={draft.dateFrom}
              onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
              inputProps={{ "aria-label": i18n.t("ratings.filters.dateFrom") }}
              InputProps={datePrefix(i18n.t("ratings.filters.dateFrom"))}
            />
            <TextField
              size="small"
              variant="outlined"
              className={classes.filterDate}
              type="date"
              value={draft.dateTo}
              onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
              inputProps={{ "aria-label": i18n.t("ratings.filters.dateTo") }}
              InputProps={datePrefix(i18n.t("ratings.filters.dateTo"))}
            />
            <TextField
              select
              size="small"
              variant="outlined"
              className={classes.filterSelect}
              value={draft.userId}
              onChange={(e) => setDraft((d) => ({ ...d, userId: e.target.value }))}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  const current = users.find((u) => String(u.id) === String(value));
                  return prefixed(
                    i18n.t("ratings.filters.attendant"),
                    value === ALL || !current ? i18n.t("ratings.filters.allAttendants") : current.name
                  );
                },
              }}
            >
              <MenuItem value={ALL}>{i18n.t("ratings.filters.allAttendants")}</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              className={classes.filterSelect}
              value={draft.queueId}
              onChange={(e) => setDraft((d) => ({ ...d, queueId: e.target.value }))}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  const current = queues.find((q) => String(q.id) === String(value));
                  return prefixed(
                    i18n.t("ratings.filters.queue"),
                    value === ALL || !current ? i18n.t("ratings.filters.allQueues") : current.name
                  );
                },
              }}
            >
              <MenuItem value={ALL}>{i18n.t("ratings.filters.allQueues")}</MenuItem>
              {queues.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" className={classes.filterButton} onClick={applyFilters}>
              {i18n.t("ratings.filters.apply")}
            </Button>
          </div>

          {/* Cards de topo */}
          <Grid2 container spacing={2}>
            {cards.map((c) => (
              <Grid2 xs={12} sm={6} md={4} lg key={c.label}>
                <StatCard label={c.label} value={loadingSummary && summary.total === 0 ? "…" : c.value} icon={c.icon} tone={c.tone} />
              </Grid2>
            ))}
          </Grid2>

          {/* Por atendente */}
          <Section
            flush
            title={sectionTitle(i18n.t("ratings.attendants.title"), i18n.t("ratings.attendants.hint"), classes)}
          >
            {loadingSummary && attendants.length === 0 ? (
              <div className={classes.loadingRow}>
                <CircularProgress size={24} />
              </div>
            ) : attendants.length === 0 ? (
              <EmptyState icon={<PeopleOutlineIcon />} title={i18n.t("ratings.attendants.empty")} />
            ) : (
              <div className={classes.tableWrap}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("ratings.attendants.name")}</TableCell>
                      <TableCell align="right">{i18n.t("ratings.attendants.avg")}</TableCell>
                      <TableCell align="right">{i18n.t("ratings.attendants.count")}</TableCell>
                      <TableCell>{i18n.t("ratings.attendants.distribution")}</TableCell>
                      <TableCell align="right">{i18n.t("ratings.attendants.closedTickets")}</TableCell>
                      <TableCell align="right">{i18n.t("ratings.attendants.responseRate")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendants.map((a) => {
                      const kind = rateKind(a.avg);
                      // sem avaliação no período a média 0,0 não é nota ruim: sai sem cor
                      const rated = Number(a.count) > 0;
                      return (
                        <TableRow key={a.userId}>
                          <TableCell>
                            <span className={classes.contactName}>{a.name}</span>
                          </TableCell>
                          <TableCell align="right">
                            <span className={classes.avg} style={{ color: rated ? kindColor(t, kind) : t.textMuted }}>
                              <StarRateIcon className={classes.avgStar} />
                              {fmtAvg(a.avg)}
                            </span>
                          </TableCell>
                          <TableCell align="right" className={a.count > 0 ? classes.num : classes.numMuted}>
                            {a.count || 0}
                          </TableCell>
                          <TableCell>
                            <span className={classes.distribution}>
                              {KINDS.map((k) => {
                                const KindIcon = KIND_ICON[k];
                                return (
                                  <span
                                    key={k}
                                    className={classes.distItem}
                                    style={{ color: kindColor(t, k) }}
                                    title={i18n.t(`ratings.labels.${k}`)}
                                  >
                                    <KindIcon className={classes.kindIcon} aria-hidden="true" />
                                    {a[k] || 0}
                                  </span>
                                );
                              })}
                            </span>
                          </TableCell>
                          <TableCell align="right">{a.closedTickets || 0}</TableCell>
                          <TableCell align="right">{fmtPct(a.responseRate)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Section>

          {/* Lista de avaliações */}
          <Section
            flush
            title={sectionTitle(i18n.t("ratings.list.title"), i18n.t("ratings.list.hint"), classes)}
          >
            {loadingList && records.length === 0 ? (
              <div className={classes.loadingRow}>
                <CircularProgress size={24} />
              </div>
            ) : records.length === 0 ? (
              <EmptyState
                icon={<StarRateIcon />}
                title={i18n.t("ratings.list.emptyTitle")}
                message={i18n.t("ratings.list.emptyMessage")}
              />
            ) : (
              <>
                <div className={classes.tableWrap}>
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{i18n.t("ratings.list.date")}</TableCell>
                        <TableCell>{i18n.t("ratings.list.contact")}</TableCell>
                        <TableCell>{i18n.t("ratings.list.attendant")}</TableCell>
                        <TableCell>{i18n.t("ratings.list.queue")}</TableCell>
                        <TableCell>{i18n.t("ratings.list.rate")}</TableCell>
                        <TableCell align="right">{i18n.t("ratings.list.ticket")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map((r) => {
                        const kind = rateKind(r.rate, r.label);
                        const KindIcon = KIND_ICON[kind];
                        return (
                          <TableRow key={r.id}>
                            <TableCell>{r.createdAt ? moment(r.createdAt).format("DD/MM/YYYY HH:mm") : "—"}</TableCell>
                            <TableCell>
                              <div className={classes.contactName}>{r.contactName || "—"}</div>
                              {r.contactNumber && <div className={classes.contactNumber}>{r.contactNumber}</div>}
                            </TableCell>
                            <TableCell>{r.userName || <span className={classes.numMuted}>—</span>}</TableCell>
                            <TableCell>{r.queueName || <span className={classes.numMuted}>—</span>}</TableCell>
                            <TableCell>
                              <StatusBadge
                                color={KIND_TONE[kind]}
                                icon={<KindIcon className={classes.kindIcon} aria-hidden="true" />}
                                label={
                                  <>
                                    {r.label || i18n.t(`ratings.labels.${kind}`)}{" "}
                                    <span style={{ opacity: 0.75 }}>· {Number.isFinite(Number(r.rate)) ? Number(r.rate) : "—"}</span>
                                  </>
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              {r.ticketUuid ? (
                                <RouterLink to={`/tickets/${r.ticketUuid}`} className={classes.ticketLink}>
                                  #{r.ticketId}
                                  <OpenInNewIcon style={{ fontSize: 14 }} />
                                </RouterLink>
                              ) : (
                                <span className={classes.numMuted}>#{r.ticketId || "—"}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className={classes.pager}>
                  <span>{i18n.t("ratings.list.showing", { from, to, count })}</span>
                  <div className={classes.pagerButtons}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={pageNumber <= 1 || loadingList}
                      onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    >
                      {i18n.t("ratings.list.prev")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasMore || loadingList}
                      onClick={() => setPageNumber((p) => p + 1)}
                    >
                      {i18n.t("ratings.list.next")}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Section>
        </div>
      </div>
    </MainContainer>
  );
};

export default Ratings;
